import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { AppLayout } from './components/layout/AppLayout';
import { useAuth } from './lib/hooks/useAuth';
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import Contacts from './pages/Contacts';
import Calendar from './pages/Calendar';
import Workflows from './pages/Workflows';
import Integrations from './pages/Integrations';
import Settings from './pages/Settings';
import Conversations from './pages/Conversations';
import Import from './pages/Import';
import AIAssistant from './pages/AIAssistant';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DeleteAccount from './pages/DeleteAccount';
import Communications from './pages/Communications';
import Billing from './pages/Billing';
import AdminConsole from './pages/AdminConsole';
import Admin from './pages/Admin';
import Credits from './pages/Credits';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Dispatch from './pages/Dispatch';
import Memberships from './pages/Memberships';

// Platform detection for hiding payment routes on iOS (Apple 3.1.1 IAP requirement)
const IS_NATIVE_IOS =
  typeof window !== 'undefined' &&
  (window as any).Capacitor?.getPlatform?.() === 'ios';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Admin emails - add authorized admin emails here
const ADMIN_EMAILS = [
  'admin@wickedcrm.com',
  'wyatt@fanz.website',
  'wyattxxxcole@gmail.com',
];

export function isAdminUser(email: string | undefined): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Billing gate removed - all authenticated users have access
  // To re-enable billing, uncomment the following:
  // const { user } = useAuth();
  // const location = useLocation();
  // const hasAccess = Boolean(user?.comped) || Boolean(user?.active_subscription);
  // if (!hasAccess && location.pathname !== '/billing') return <Navigate to="/billing" replace />;

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdminUser(user?.email)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

const App = () => (
  <ThemeProvider>
    <AccessibilityProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/delete-account" element={<DeleteAccount />} />

              {/* Protected Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/comms" element={<Communications />} />
                <Route path="/conversations" element={<Conversations />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/workflows" element={<Workflows />} />
                {/* Integrations hidden on iOS — has API key config (Apple 3.1.1) */}
                <Route path="/integrations" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Integrations />} />
                <Route path="/import" element={<Import />} />
                <Route path="/ai" element={<AIAssistant />} />
                <Route path="/billing" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Billing />} />
                <Route path="/dispatch" element={<Dispatch />} />
                {/* Payment routes — redirected to / on iOS to comply with Apple 3.1.1 (IAP required for digital goods) */}
                <Route path="/memberships" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Memberships />} />
                <Route path="/credits" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Credits />} />
                <Route path="/cart" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Cart />} />
                <Route path="/checkout" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <Checkout />} />
                {/* Admin page hidden on iOS — has API key config which Apple flags as IAP bypass (3.1.1) */}
                <Route path="/admin" element={IS_NATIVE_IOS ? <Navigate to="/" replace /> : <AdminRoute><Admin /></AdminRoute>} />
                <Route path="/settings" element={<Settings />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </AccessibilityProvider>
  </ThemeProvider>
);

export default App;
